import '../../styles/imageClassStyles.css'

export type MagazineRenderSurface =
  | 'postPreview'
  | 'postPublished'
  | 'peoplePreview'
  | 'peopleAbout'
  | 'microPreview'

export const MAGAZINE_ALLOWED_TAGS = [
  'a',
  'p',
  'span',
  'strong',
  'em',
  'u',
  's',
  'mark',
  'code',
  'pre',
  'blockquote',
  'hr',
  'br',
  'ul',
  'ol',
  'li',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'img',
  'figure',
  'figcaption',
  'div',
  'section',
]

export const MAGAZINE_ALLOWED_ATTR = [
  'href',
  'title',
  'target',
  'rel',
  'src',
  'alt',
  'width',
  'height',
  'class',
  'id',
  'data-link-href',
  'data-align',
  'data-wrap',
  'data-width',
  'data-size',
]

const SURFACE_CLASSES: Record<MagazineRenderSurface, string> = {
  postPreview: 'tiptap-mag prose prose-invert post-body max-w-none break-words',
  postPublished: 'prose prose-invert tiptap-mag post-body max-w-none mt-6 chat-scroll break-words',
  peoplePreview: 'tiptap-mag prose prose-invert prose-base max-w-none prose-p:my-2 prose-li:my-1',
  peopleAbout: 'tiptap-mag prose prose-invert prose-base max-w-none prose-p:my-2 prose-li:my-1',
  microPreview: 'prose prose-invert tiptap-mag max-w-none break-words text-white/90',
}

function hasToken(tokens: string[], ...want: string[]) {
  return want.some((token) => tokens.includes(token))
}

export function getMagazineImageClassDecision(className: string) {
  const tokens = String(className || '')
    .split(/\s+/)
    .map((v) => v.trim())
    .filter(Boolean)

  const size = hasToken(tokens, 'mag-image--xsmall', 'mag-image--xs', 'px-image--xsmall', 'px-image--xs')
    ? 'xsmall'
    : hasToken(tokens, 'mag-image--small', 'mag-image--s', 'px-image--small', 'px-image--s')
    ? 'small'
    : hasToken(tokens, 'mag-image--medium', 'mag-image--m', 'px-image--medium', 'px-image--m')
    ? 'medium'
    : hasToken(tokens, 'mag-image--large', 'mag-image--l', 'px-image--large', 'px-image--l')
    ? 'large'
    : 'small'

  const align = hasToken(tokens, 'mag-image--left', 'px-image--left')
    ? 'left'
    : hasToken(tokens, 'mag-image--center', 'px-image--center')
    ? 'center'
    : 'right'

  const wrap = hasToken(tokens, 'mag-image--tight', 'px-image--tight')
    ? 'tight'
    : hasToken(tokens, 'mag-image--airy', 'px-image--airy')
    ? 'airy'
    : 'normal'

  return { size, align, wrap }
}

export function linkifyMagazineImageNodes(input: string): string {
  const html = String(input || '')
  if (!html.trim()) return html
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html')
    const images = Array.from(doc.querySelectorAll('img[data-link-href]'))
    images.forEach((img) => {
      const href = String(img.getAttribute('data-link-href') || '').trim()
      if (!href || /^\s*javascript:/i.test(href)) return
      const parent = img.parentElement
      if (parent && parent.tagName === 'A') {
        parent.setAttribute('href', href)
        parent.setAttribute('target', '_blank')
        parent.setAttribute('rel', 'noopener noreferrer nofollow')
        return
      }
      const anchor = doc.createElement('a')
      anchor.setAttribute('href', href)
      anchor.setAttribute('target', '_blank')
      anchor.setAttribute('rel', 'noopener noreferrer nofollow')
      parent?.insertBefore(anchor, img)
      anchor.appendChild(img)
    })
    return doc.body.innerHTML
  } catch {
    return html
  }
}

export function sanitizeMagazineHtml(input: string): string {
  const html = String(input || '')
  try {
    const purify = (window as any)?.DOMPurify
    if (purify?.sanitize) {
      return purify.sanitize(html, {
        ALLOWED_TAGS: MAGAZINE_ALLOWED_TAGS,
        ALLOWED_ATTR: MAGAZINE_ALLOWED_ATTR,
        FORBID_TAGS: ['script', 'style'],
      })
    }
  } catch {}
  return html
}

export function prepareMagazineHtml(input: string): string {
  return sanitizeMagazineHtml(linkifyMagazineImageNodes(input))
}

export function createMagazineRenderConfig(surface: MagazineRenderSurface) {
  return {
    surface,
    className: SURFACE_CLASSES[surface],
    prepareHtml: prepareMagazineHtml,
    sanitizeHtml: sanitizeMagazineHtml,
    imageClassDecision: getMagazineImageClassDecision,
  }
}
